import { api } from './api';

/**
 * Two-factor authentication — web client for the EXISTING `/mfa` module.
 *
 * There is one MFA implementation and it is the server's: TOTP (RFC 6238) with single-use
 * recovery codes, in `apps/api/src/lib/mfa.ts`. Nothing here verifies a code, derives a secret,
 * or decides whether a factor is satisfied — this file is transport only. A second
 * implementation on the client would be a second thing to get wrong and could not be trusted
 * anyway, because the browser is the side under attack.
 *
 * ── WHAT NEVER GOES ANYWHERE NEAR A LOG ───────────────────────────────────────────────────────
 * The TOTP secret, the QR data URL (which CONTAINS the secret), typed codes and recovery codes
 * are returned to the caller and rendered; they are never passed to `console.*`, never put in a
 * query string, and never persisted. Recovery codes are shown exactly once, by the server's
 * design — `issueBackupCodes` stores only a SHA-256 hash, so a lost set can be replaced but
 * never re-read.
 */

export interface MfaStatus {
  mfaEnabled: boolean;
  verifiedAt: string | null;
  remainingBackupCodes: number;
}

export interface MfaSetupBegin {
  /** Base32 TOTP secret, for manual entry when a camera is not available. */
  secret: string;
  /** `data:image/png;base64,…` encoding the otpauth:// URI. Contains the secret. */
  qrCodeDataUrl: string;
}

export async function fetchMfaStatus(): Promise<MfaStatus> {
  const res = await api.get<{ data: MfaStatus }>('/mfa/status');
  return res.data.data;
}

export async function beginMfaSetup(): Promise<MfaSetupBegin> {
  const res = await api.post<{ data: MfaSetupBegin }>('/mfa/setup/begin', {});
  return res.data.data;
}

/** Confirms the first code and arms MFA. Returns the recovery codes — shown once, then gone. */
export async function confirmMfaSetup(code: string): Promise<string[]> {
  const res = await api.post<{ data: { backupCodes: string[] } }>('/mfa/setup/confirm', {
    code: code.replace(/\s/g, ''),
  });
  return res.data.data.backupCodes;
}

/**
 * Turning MFA off, or replacing the recovery codes, both require proof of the second factor —
 * by authenticator OR by recovery code. The recovery-code route exists so that losing the phone
 * is recoverable: it is the difference between "I must re-enrol" and "I am locked out forever".
 */
export async function disableMfa(code: string, useRecoveryCode = false): Promise<void> {
  await api.delete('/mfa', { data: { code: code.replace(/\s/g, ''), useRecoveryCode } });
}

export async function regenerateBackupCodes(code: string, useRecoveryCode = false): Promise<string[]> {
  const res = await api.post<{ data: { backupCodes: string[] } }>('/mfa/recovery-codes/regenerate', {
    code: code.replace(/\s/g, ''),
    useRecoveryCode,
  });
  return res.data.data.backupCodes;
}

/**
 * The second step of signing in.
 *
 * Deliberately NOT routed through `features/auth/api.ts`: this call is made while the user has
 * no session at all, holding only the short-lived challenge token the password step returned.
 * The server sets the refresh cookie and returns the same session payload a password-only login
 * returns, so the caller can hand it straight to `setSession`.
 */
export interface MfaVerifyResult {
  accessToken: string;
  user: import('@/features/auth/types').AuthUser;
  chambers: import('@/features/auth/types').AuthChambers;
  permissions: string[];
}

export async function verifyMfaChallenge(input: {
  challengeToken: string;
  code: string;
  useRecoveryCode?: boolean;
}): Promise<MfaVerifyResult> {
  const res = await api.post<{ data: MfaVerifyResult }>('/mfa/verify', {
    challengeToken: input.challengeToken,
    code: input.code.replace(/\s/g, ''),
    useRecoveryCode: input.useRecoveryCode ?? false,
  });
  return res.data.data;
}
